import { Box, Flex, Heading, Text } from '@chakra-ui/react'
import { ReactNode } from 'react'

import { useDocumentTitle } from '../../helpers'

export interface IPageTitleProps {
  /** Título da página, em pt-BR. Renderizado em caixa alta pelo próprio componente. */
  title: ReactNode
  /** Linha de apoio opcional, explicando o que a página mostra. */
  description?: ReactNode
  /** Ações que acompanham o título (botões de contexto, filtros globais, etc). */
  actions?: ReactNode
  /** Espaçamento inferior. Só mude quando a página já tiver um bloco de espaçamento próprio logo abaixo. */
  mb?: string | number
  /** Desligue apenas quando o título for o nome de um registro (um evento, um concurso). */
  uppercase?: boolean
  /**
   * Título da aba, para quando o `title` for um `ReactNode` e não uma string. Quando o `title`
   * já é texto ele vale como está e esta prop não é necessária.
   */
  documentTitle?: string
}

/**
 * Cabeçalho padrão das páginas do painel: um único `h1` em caixa alta,
 * com descrição e ações opcionais alinhadas à direita.
 *
 * Toda página do dashboard deve começar por ele — não monte títulos com
 * `<Heading>` solto.
 */
export default function PageTitle({
  title,
  description,
  actions,
  mb = '8',
  uppercase = true,
  documentTitle
}: IPageTitleProps) {
  // O título da aba sai do mesmo lugar que o `h1`: é a mesma informação, e nenhuma das ~104
  // páginas escrevia `document.title` por conta própria.
  useDocumentTitle(documentTitle ?? (typeof title === 'string' ? title : undefined))

  return (
    <Flex
      mb={mb}
      gap="4"
      wrap="wrap"
      direction={{ base: 'column', md: 'row' }}
      align={{ base: 'stretch', md: 'center' }}
      justify="space-between"
    >
      <Box>
        <Heading
          as="h1"
          color="gray.800"
          fontWeight="700"
          lineHeight="1.2"
          textTransform={uppercase ? 'uppercase' : 'none'}
        >
          {title}
        </Heading>
        {description && (
          // `as="div"`: descrições podem trazer botões e outros blocos, que não podem viver dentro de um <p>.
          <Text variant="caption" as="div" mt="1">
            {description}
          </Text>
        )}
      </Box>
      {actions && (
        <Flex align="center" gap="2" flexShrink={0} wrap="wrap">
          {actions}
        </Flex>
      )}
    </Flex>
  )
}
